import { colorStrings, mediaQueriesBP } from "@iewccouk/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    title = "title",
    description = "description",
    maxNumber = "maxNumber",
    spacingTop = "spacingTop",
    spacingBetweenCards = "spacingBetweenCards",
    spacingBottom = "spacingBottom",

    displayTitle = "Group title",
    displayDescription = "Group description",
    displayMaxNumber = "Max number to show per row",
    displaySpacingTop = "Spacing top",
    displaySpacingBetweenCards = "Spacing between cards",
    displaySpacingBottom = "Spacing bottom",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.maxNumber]: number;
        [fields.spacingTop]: number;
        [fields.spacingBetweenCards]: number;
        [fields.spacingBottom]: number;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

export interface VideoCardGroupWidgetStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    cardWrapperEditMode?: InjectableCss;
    cardWrapper?: InjectableCss;
    dotsGroup?: InjectableCss;
}

export const videoCardGroupWidgetStyles: VideoCardGroupWidgetStyles = {
    cardWrapperEditMode: {
        css: css`
            [class*="ZoneWrapper-"] {
                display: flex;
                flex-wrap: wrap;

                [class*="AddContainer-"] {
                    width: 100%;
                }
            }
        `,
    },
};

export const styles = videoCardGroupWidgetStyles;

export const VideoCardGroupWidget = ({
    id,
    fields: { title, description, maxNumber, spacingTop, spacingBetweenCards, spacingBottom },
    shellContext: { isEditing },
}: Props) => {
    const spacing = spacingBetweenCards * (maxNumber - 1);
    const text = `calc((100% / ${maxNumber}) - (${spacing}px / ${maxNumber}))`;
    const sizes = css`
        ${mediaQueriesBP.minMD} {
            margin-right: ${spacingBetweenCards}px;
            max-width: ${text};
            width: 100%;

            &:nth-child(${maxNumber}n) {
                margin-right: 0;
            }

            &:nth-child(n + ${maxNumber + 1}) {
                margin-top: ${spacingBetweenCards}px;
            }
        }
    `;

    const cartWrapperStyle: InjectableCss = {};
    if (isEditing) {
        cartWrapperStyle.css = css`
            ${styles.cardWrapperEditMode?.css}

            [class*="ZoneWrapper-"] {
                & > *:not([class*="AddContainer-"]) {
                    ${sizes}
                }
            }
        `;
    } else {
        cartWrapperStyle.css = css`
            ${styles.cardWrapper?.css}

            ${mediaQueriesBP.minMD} {
                & > [class*="Wrapper-"] {
                    & > * {
                        ${sizes}
                    }
                }
            }
        `;
    }

    const wrapperStyle = styles.wrapper ?? {};
    wrapperStyle.css = css`
        ${wrapperStyle?.css}
        margin-top: ${spacingTop ?? 0}px;
        margin-bottom: ${spacingBottom ?? 0}px;
    `;

    return (
        <StyledWrapper {...wrapperStyle}>
            <Typography {...styles.title}>{title}</Typography>

            {description && <Typography {...styles.description}>{parse(description, parserOptions)}</Typography>}

            {isEditing && (
                <>
                    <Typography variant="legend" color={colorStrings.dangerMain}>
                        {translate("The VideoCard widget is the only one optimized to be added here")}
                    </Typography>

                    <StyledWrapper {...cartWrapperStyle}>
                        <Zone contentId={id as never} zoneName={"VideoCards" as never} />
                    </StyledWrapper>
                </>
            )}

            {!isEditing && (
                <StyledWrapper {...cartWrapperStyle}>
                    <Zone contentId={id as never} zoneName={"VideoCards" as never} fixed={true as never} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const definitionTab: TabDefinition = {
    displayName: "Definition",
    sortOrder: 1,
};

const stylesTab: TabDefinition = {
    displayName: "Styles",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(VideoCardGroupWidget)) as any,
    definition: {
        displayName: "Video Card Group",
        group: "Verndale Box" as any,
        icon: "presentation-screen",
        fieldDefinitions: [
            // Definition tab items:
            {
                tab: definitionTab,
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                tab: definitionTab,
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                tab: definitionTab,
                name: fields.maxNumber,
                displayName: fields.displayMaxNumber,
                editorTemplate: "IntegerField",
                defaultValue: 2,
                min: 1,
                max: 3,
                fieldType: "General",
            },

            // Styles tab items:
            {
                tab: stylesTab,
                name: fields.spacingTop,
                displayName: fields.displaySpacingTop,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            {
                tab: stylesTab,
                name: fields.spacingBetweenCards,
                displayName: fields.displaySpacingBetweenCards,
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
            },
            {
                tab: stylesTab,
                name: fields.spacingBottom,
                displayName: fields.displaySpacingBottom,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
